import React, { FC, ReactNode } from 'react';
import styles from '../journey-explorer.module.scss';
import { JourneyKpis } from '../../../../models/journey-explorer/journey-explorer';
import { formatCompact, formatDuration, formatNumber, formatSpan } from '../journey-explorer-utils';
import { CompareData } from '../journey-compare';

const EMPTY = '–';

const isFiltered = (kpis: JourneyKpis) =>
	kpis.customers !== kpis.customers_unfiltered || kpis.interactions !== kpis.interactions_unfiltered;

const reachedText = (kpis: JourneyKpis, goalLabel: string) =>
	`${formatNumber(kpis.reached_customers)} of ${formatNumber(kpis.customers)} customers reached ${goalLabel}`;

const customersCaption = (kpis: JourneyKpis) => {
	if (kpis.customers === kpis.customers_unfiltered || kpis.customers_unfiltered <= 0) return 'All customers';
	const share = (kpis.customers / kpis.customers_unfiltered) * 100;
	return `${share < 1 ? share.toFixed(1) : Math.round(share)}% of all customers`;
};

const timeCaption = (kpis: JourneyKpis) => {
	const all = kpis.median_secs_to_goal_unfiltered;
	const secs = kpis.median_secs_to_goal;
	if (!isFiltered(kpis) || all === null || all === undefined || secs === null) return 'Median, first visit to goal';
	const diff = secs - all;
	if (Math.abs(diff) < Math.max(60, all * 0.05)) return 'Same as all customers';
	return `${formatSpan(Math.abs(diff))} ${diff < 0 ? 'faster' : 'slower'} than all`;
};

const compareVerdict = (a: JourneyKpis | undefined, b: JourneyKpis | undefined) => {
	if (!a || !b) return null;
	const rateA = a.goal_rate_pct;
	const rateB = b.goal_rate_pct;
	if (rateA <= 0 || rateB <= 0) return null;
	const ratio = Math.max(rateA, rateB) / Math.min(rateA, rateB);
	if (ratio < 1.05) return 'Similar goal rate';
	return rateA > rateB ? `A reaches it ${ratio.toFixed(1)}× B` : `B reaches it ${ratio.toFixed(1)}× A`;
};

const Bar: FC<{ pct: number; tone: string }> = ({ pct, tone }) => (
	<div className={styles.kpi_bar_track}>
		<span className={`${styles.kpi_bar_fill} ${tone}`} style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
	</div>
);

interface TileProps {
	label: string;
	chip?: ReactNode;
	hero?: boolean;
	children: ReactNode;
}

const Tile: FC<TileProps> = ({ label, chip, hero, children }) => (
	<div className={`${styles.kpi} ${hero ? styles.kpi_hero : ''}`}>
		<div className={styles.kpi_head}>
			<span className={styles.kpi_label}>{label}</span>
			{chip}
		</div>
		{children}
	</div>
);

const Skeleton: FC<{ wide?: boolean }> = ({ wide }) => (
	<span className={`${styles.kpi_skeleton} ${wide ? styles.kpi_skeleton_wide : ''}`} aria-label="Loading" />
);

interface CompareRowProps {
	side: 'a' | 'b';
	value: string;
	note?: string;
	loading: boolean;
	waiting: boolean;
	title: string;
}

const CompareRow: FC<CompareRowProps> = ({ side, value, note, loading, waiting, title }) => (
	<div className={styles.kpi_compare_row} title={title}>
		<span className={`${styles.kpi_side} ${side === 'a' ? styles.compare_a : styles.compare_b}`}>
			{side.toUpperCase()}
		</span>
		{waiting ? (
			<Skeleton />
		) : (
			<>
				<span className={`${styles.kpi_compare_value} ${loading ? styles.kpi_updating : ''}`}>{value}</span>
				{note && <span className={styles.kpi_compare_note}>{note}</span>}
			</>
		)}
	</div>
);

interface KpiBarProps {
	kpis: JourneyKpis | null;
	loading: boolean;
	goalLabel: string | null;
	compare?: CompareData | null;
}

const KpiBar: FC<KpiBarProps> = ({ kpis, loading, goalLabel, compare }) => {
	if (compare) {
		const a = compare.a.result?.kpis;
		const b = compare.b.result?.kpis;
		const verdict = goalLabel ? compareVerdict(a, b) : null;
		const rows = (render: (side: JourneyKpis) => { value: string; note?: string }) =>
			(['a', 'b'] as const).map((side) => {
				const data = side === 'a' ? a : b;
				const shown = data ? render(data) : { value: EMPTY };
				return (
					<CompareRow
						key={side}
						side={side}
						value={shown.value}
						note={shown.note}
						loading={loading}
						waiting={loading && !data}
						title={compare[side].label}
					/>
				);
			});

		return (
			<div className={styles.kpi_bar} aria-busy={loading}>
				<div className={styles.kpi_strip}>
					<Tile
						label="Goal rate"
						hero
						chip={verdict && <span className={styles.kpi_chip_flat}>{verdict}</span>}
					>
						{(['a', 'b'] as const).map((side) => {
							const data = side === 'a' ? a : b;
							return (
								<div key={side} className={styles.kpi_compare_goal}>
									<CompareRow
										side={side}
										value={data && goalLabel ? `${data.goal_rate_pct.toFixed(1)}%` : EMPTY}
										note={
											data && goalLabel
												? `${formatNumber(data.reached_customers)} of ${formatNumber(
														data.customers
												  )}`
												: undefined
										}
										loading={loading}
										waiting={loading && !data}
										title={compare[side].label}
									/>
									<Bar
										pct={goalLabel ? data?.goal_rate_pct ?? 0 : 0}
										tone={side === 'a' ? styles.kpi_bar_a : styles.kpi_bar_b}
									/>
								</div>
							);
						})}
					</Tile>
					<Tile label="Customers">{rows((side) => ({ value: formatNumber(side.customers) }))}</Tile>
					<Tile label="Time to goal">
						{rows((side) => ({ value: goalLabel ? formatDuration(side.median_secs_to_goal) : EMPTY }))}
					</Tile>
					<Tile label="Activity">
						{rows((side) => ({
							value: formatCompact(side.interactions),
							note: `${side.avg_journey.toFixed(1)} each`,
						}))}
					</Tile>
				</div>
			</div>
		);
	}

	const waiting = loading && !kpis;
	const updating = loading ? styles.kpi_updating : '';
	const filtered = kpis ? isFiltered(kpis) : false;
	const delta =
		kpis && goalLabel && filtered
			? Math.round((kpis.goal_rate_pct - kpis.goal_rate_pct_unfiltered) * 10) / 10
			: null;
	let chip: ReactNode = null;
	if (delta !== null && !loading) {
		const tone = delta > 0 ? styles.kpi_chip_good : delta < 0 ? styles.kpi_chip : styles.kpi_chip_flat;
		chip = (
			<span className={`${styles.kpi_chip} ${tone}`}>
				{delta > 0 ? '+' : ''}
				{delta.toFixed(1)} pts vs all
			</span>
		);
	}

	const value = (text: string, className = styles.kpi_value) =>
		waiting ? (
			<Skeleton wide={className !== styles.kpi_value} />
		) : (
			<div className={`${className} ${updating}`}>{text}</div>
		);
	const valueRow = (text: string, note: string, className?: string) => (
		<div className={styles.kpi_value_row}>
			{value(text, className)}
			{kpis && note && (
				<span className={styles.kpi_caption} title={note}>
					{note}
				</span>
			)}
		</div>
	);

	return (
		<div className={styles.kpi_bar} aria-busy={loading}>
			<div className={styles.kpi_strip}>
				<Tile label="Goal rate" hero chip={chip}>
					{valueRow(
						kpis && goalLabel ? `${kpis.goal_rate_pct.toFixed(1)}%` : EMPTY,
						kpis && goalLabel ? reachedText(kpis, goalLabel) : 'Choose a goal in the sidebar',
						styles.kpi_value_hero
					)}
				</Tile>
				<Tile label="Customers">
					{valueRow(kpis ? formatNumber(kpis.customers) : EMPTY, kpis ? customersCaption(kpis) : '')}
				</Tile>
				<Tile label="Time to goal">
					{valueRow(
						kpis && goalLabel && kpis.median_secs_to_goal !== null
							? formatSpan(kpis.median_secs_to_goal)
							: EMPTY,
						kpis && goalLabel ? timeCaption(kpis) : ''
					)}
				</Tile>
				<Tile label="Activity">
					{valueRow(
						kpis ? formatCompact(kpis.interactions) : EMPTY,
						kpis ? `${kpis.avg_journey.toFixed(1)} per customer` : ''
					)}
				</Tile>
			</div>
		</div>
	);
};

export default KpiBar;
